// Configure POS integration

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRestaurant } from '../../../providers/RestaurantContext';
import { db } from '../../../config/firebase/config';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { POSProvider, POSConfig } from '../../../services/pos/types';
import { POSAdapterFactory } from '../../../services/pos/POSAdapterFactory';
import { createPOSService } from '../../../services/pos/posService';
import { Settings, CheckCircle, XCircle, Loader } from 'lucide-react';

const POSConfigScreen: React.FC = () => {
  const navigate = useNavigate();
  const { restaurant, userRole } = useRestaurant();

  // Form state (all hooks must be called unconditionally)
  const [provider, setProvider] = useState<POSProvider>('mock');
  const [enabled, setEnabled] = useState(false);
  const [autoSync, setAutoSync] = useState(true);
  const [syncInterval, setSyncInterval] = useState(2);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [accountId, setAccountId] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (userRole != null && userRole !== 'super_admin') {
      navigate('/admin', { replace: true });
    }
  }, [userRole, navigate]);

  useEffect(() => {
    if (restaurant?.posConfig) {
      setProvider(restaurant.posConfig.provider);
      setEnabled(restaurant.posConfig.enabled);
      setAutoSync(restaurant.posConfig.syncSettings?.autoSync ?? true);
      setSyncInterval(restaurant.posConfig.syncSettings?.syncIntervalMinutes ?? 2);
      if (restaurant.posConfig.credentials) {
        setApiKey(restaurant.posConfig.credentials.apiKey || '');
        setApiSecret(restaurant.posConfig.credentials.apiSecret || '');
        setAccountId(restaurant.posConfig.credentials.accountId || '');
      }
    }
  }, [restaurant?.posConfig]);

  if (userRole != null && userRole !== 'super_admin') {
    return null;
  }

  const handleTestConnection = async () => {
    if (!restaurant?.id) return;

    setIsTesting(true);
    setTestResult(null);

    try {
      const testConfig: POSConfig = {
        provider,
        enabled: true,
        credentials: {
          apiKey,
          apiSecret,
          accountId
        }
      };

      // Validate config
      POSAdapterFactory.validateConfig(testConfig);

      // Create service and test
      const service = createPOSService(restaurant.id, testConfig);
      const connected = await service.testConnection();

      if (connected) {
        setTestResult('success');
        toast.success('✅ Conexión exitosa con el POS');
      } else {
        setTestResult('error');
        toast.error('❌ No se pudo conectar con el POS');
      }
    } catch (error) {
      console.error('Error testing POS connection:', error);
      setTestResult('error');
      toast.error(`Error: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (!restaurant?.id) return;

    setIsSaving(true);

    try {
      const posConfig: POSConfig = {
        provider,
        enabled,
        credentials: {
          apiKey: apiKey.trim(),
          apiSecret: apiSecret.trim(),
          accountId: accountId.trim()
        },
        syncSettings: {
          autoSync,
          syncIntervalMinutes: syncInterval
        }
      };

      // Validate before saving
      POSAdapterFactory.validateConfig(posConfig);

      const restaurantRef = doc(db, 'restaurants', restaurant.id);
      await updateDoc(restaurantRef, {
        posConfig,
        updatedAt: serverTimestamp()
      });

      toast.success('✅ Configuración de POS guardada');
      
    } catch (error) {
      console.error('Error saving POS config:', error);
      toast.error(`Error al guardar: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const renderCredentialsFields = () => {
    switch (provider) {
      case 'mock':
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              ℹ️ <strong>Mock POS</strong> es solo para pruebas. No requiere credenciales.
            </p>
          </div>
        );

      case 'inflo':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key de InFlo
              </label>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Ingresa tu API Key de InFlo"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account ID
              </label>
              <input
                type="text"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                placeholder="Tu Account ID de InFlo"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        );

      case 'alegra':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key de Alegra
              </label>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Ingresa tu API Key de Alegra"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Secret
              </label>
              <input
                type="password"
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                placeholder="Tu API Secret de Alegra"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-semibold">Configuración de POS</h1>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            ← Volver
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
          
          {/* Provider Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sistema POS
            </label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value as POSProvider)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="mock">Mock POS (Pruebas)</option>
              <option value="inflo">InFlo POS</option>
              <option value="alegra">Alegra POS</option>
              <option value="toast">Toast POS</option>
              <option value="square">Square POS</option>
            </select>
          </div>

          {/* Credentials */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Credenciales</h3>
            {renderCredentialsFields()}
          </div>

          {/* Test Connection */}
          <div>
            <button
              onClick={handleTestConnection}
              disabled={isTesting}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              {isTesting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Probando conexión...
                </>
              ) : (
                <>
                  {testResult === 'success' && <CheckCircle className="w-4 h-4 text-green-600" />}
                  {testResult === 'error' && <XCircle className="w-4 h-4 text-red-600" />}
                  Probar Conexión
                </>
              )}
            </button>
            {testResult === 'success' && (
              <p className="text-sm text-green-600 mt-2">✅ Conexión exitosa</p>
            )}
            {testResult === 'error' && (
              <p className="text-sm text-red-600 mt-2">❌ Error de conexión</p>
            )}
          </div>

          {/* Enable/Disable */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="enabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded"
            />
            <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
              Habilitar integración con POS
            </label>
          </div>

          {/* Sync Settings */}
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold">Configuración de Actualización</h3>
            
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="autoSync"
                checked={autoSync}
                onChange={(e) => setAutoSync(e.target.checked)}
                className="w-5 h-5 text-blue-600 rounded"
              />
              <label htmlFor="autoSync" className="text-sm font-medium text-gray-700">
                Actualización automática
              </label>
            </div>

            {autoSync && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Intervalo de actualización (minutos)
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={syncInterval}
                  onChange={(e) => setSyncInterval(parseInt(e.target.value))}
                  className="w-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Las mesas se actualizarán cada {syncInterval} minuto(s)
                </p>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => navigate('/admin')}
              className="px-6 py-2 text-gray-600 hover:text-gray-900"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSaving ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POSConfigScreen;
